<template>
  <BaseModalLarge
    :heading="heading"
    :loading="loading"
    useForm
    :parentRoute="parentRouteName"
    @submit="submit"
  >
    <Flex direction="column">
      <BaseCard :heading="$t('customers.personal_info')">
        <Flex direction="column">
          <Flex
            :direction="$screen === 's' ? 'column' : 'row'"
            equalWidth
          >
            <BaseInput
              v-model="customer.firstName"
              focus
              :label="$t('global.first_name')"
              required
              v-test="'customer-firstName'"
            />
            <BaseInput
              v-model="customer.lastName"
              :label="$t('global.last_name')"
              required
              v-test="'customer-lastName'"
            />
          </Flex>
          <Flex
            :direction="$screen === 's' ? 'column' : 'row'"
            equalWidth
          >
            <BaseRadio
              v-model="customer.gender"
              inline
              :label="$t('global.gender')"
              :options="[
                {
                  label: $t('global.male'),
                  value: 'MALE'
                },
                {
                  label: $t('global.female'),
                  value: 'FEMALE'
                },
                {
                  label: $t('global.nonbinary'),
                  value: 'NONBINARY'
                },
                {
                  label: $t('global.unknown'),
                  value: null
                }
              ]"
              v-test="'customer-gender'"
            />
            <BaseInput
              v-model="customer.dateOfBirth"
              :label="$t('global.date_of_birth')"
              :placeholder="$t('global.manual_date')"
              type="date"
            />
          </Flex>
          <Flex
            v-if="company.medical"
            direction="column"
            equalWidth
          >
            <Flex
              equalWidth
              :direction="$screen !== 's' ? 'row' : 'column'"
            >
              <BaseInput
                v-model="customer.insuranceProvider"
                :label="$t('global.insurance_provider')"
                type="text"
                v-test="'customer-insurance-provider'"
              />
              <BaseInput
                v-model="customer.personalRegistrationNumber"
                :label="$t('global.personal_registration_number')"
                masked
                v-test="'customer-personal-registration-number'"
              />
            </Flex>
            <BaseAlert
              v-if="showBSNWarning"
              color="warning"
              :text="$t('customers.personal_registration_number_warning')"
              size="s"
            />
          </Flex>
        </Flex>
      </BaseCard>
      <BaseCard :heading="$t('customers.contact_info')">
        <Flex direction="column">
          <BaseInput
            v-model="customer.email"
            :label="$t('global.email_address')"
            :error="
                emailInvalid
                  ? $t('global.validations.invalid', {
                      value: $t('global.email_address')
                    })
                  : undefined
              "
            type="email"
            v-test="'customer-email'"
          />
          <Flex
            equalWidth
            :direction="$screen === 's' ? 'column' : 'row'"
          >
            <BaseInput
              v-model="customer.phone"
              :label="$t('global.phone_number')"
              type="tel"
              :maxLength="20"
              v-test="'customer-phone'"
            />
            <BaseInput
              v-model="customer.mobilePhone"
              :label="$t('global.mobile_number')"
              type="tel"
              :maxLength="20"
              v-test="'customer-mobilePhone'"
            />
          </Flex>
          <AddressFields
            v-model:address="customer.address"
            v-model:zipcode="customer.zipcode"
            v-model:city="customer.city"
          />
        </Flex>
      </BaseCard>
      <CustomerTags
        v-model="customerTagIds"
        v-test="'edit-customer-tags'"
      />
      <BaseCard :heading="$t('global.custom_fields')">
        <Flex direction="column">
          <BaseText>
            {{ $t('customers.add_custom_fields') }}
            <BaseText
              inline
              :routerLink="{ name: 'admin-customers' }"
            >
              {{ $t('customers.customer_settings') }}
            </BaseText>
          </BaseText>
          <Flex
            v-if="companySettings.customers.custom1Label || companySettings.customers.custom2Label"
            equalWidth
            :direction="$screen === 's' ? 'column' : 'row'"
          >
            <BaseInput
              v-if="companySettings.customers.custom1Label"
              v-model="customer.custom1"
              :label="companySettings.customers.custom1Label"
              v-test="'customer-custom1'"
            />
            <BaseInput
              v-if="companySettings.customers.custom2Label"
              v-model="customer.custom2"
              :label="companySettings.customers.custom2Label"
              v-test="'customer-custom2'"
            />
          </Flex>
          <Flex
            v-if="companySettings.customers.custom3Label || companySettings.customers.custom4Label"
            equalWidth
            :direction="$screen === 's' ? 'column' : 'row'"
          >
            <BaseInput
              v-if="companySettings.customers.custom3Label"
              v-model="customer.custom3"
              :label="companySettings.customers.custom3Label"
              v-test="'customer-custom3'"
            />
            <BaseInput
              v-if="companySettings.customers.custom4Label"
              v-model="customer.custom4"
              :label="companySettings.customers.custom4Label"
              v-test="'customer-custom4'"
            />
          </Flex>
          <Flex
            v-if="companySettings.customers.custom5Label || companySettings.customers.custom6Label"
            equalWidth
            :direction="$screen === 's' ? 'column' : 'row'"
          >
            <BaseInput
              v-if="companySettings.customers.custom5Label"
              v-model="customer.custom5"
              :label="companySettings.customers.custom5Label"
              v-test="'customer-custom5'"
            />
            <BaseInput
              v-if="companySettings.customers.custom6Label"
              v-model="customer.custom6"
              :label="companySettings.customers.custom6Label"
              v-test="'customer-custom6'"
            />
          </Flex>
          <Flex
            v-if="companySettings.customers.custom7Label || companySettings.customers.custom8Label"
            equalWidth
            :direction="$screen === 's' ? 'column' : 'row'"
          >
            <BaseInput
              v-if="companySettings.customers.custom7Label"
              v-model="customer.custom7"
              :label="companySettings.customers.custom7Label"
              v-test="'customer-custom7'"
            />
            <BaseInput
              v-if="companySettings.customers.custom8Label"
              v-model="customer.custom8"
              :label="companySettings.customers.custom8Label"
              v-test="'customer-custom8'"
            />
          </Flex>
          <Flex
            v-if="companySettings.customers.custom9Label || companySettings.customers.custom10Label"
            equalWidth
            :direction="$screen === 's' ? 'column' : 'row'"
          >
            <BaseInput
              v-if="companySettings.customers.custom9Label"
              v-model="customer.custom9"
              :label="companySettings.customers.custom9Label"
              v-test="'customer-custom9'"
            />
            <BaseInput
              v-if="companySettings.customers.custom10Label"
              v-model="customer.custom10"
              :label="companySettings.customers.custom10Label"
              v-test="'customer-custom10'"
            />
          </Flex>
        </Flex>
      </BaseCard>

      <BaseCard :heading="$t('customers.additional_info')">
        <Flex direction="column">
          <BaseInput
            v-model="customer.customerNotes"
            :label="$t('customers.additional_customer_info')"
            :info="$t('customers.additional_customer_info_tooltip')"
            v-test="'customer-customerNotes'"
          />
          <BaseInput
            v-model="customer.invoiceNotes"
            :label="$t('customers.additional_invoice_info')"
            type="textarea"
            :info="$t('customers.additional_invoice_info_tooltip')"
            v-test="'customer-invoiceNotes'"
          />
          <BaseInput
            v-model="customer.alertNotes"
            :label="$t('customers.warning_saving_appointments')"
            :info="$t('customers.warning_saving_appointments_tooltip')"
            v-test="'customer-alertNotes'"
          />
        </Flex>
      </BaseCard>
      <BaseCard :heading="$t('customers.customer_picture')">
        <UploadControls
          v-model="customer.picture"
          preset="customers"
        />
      </BaseCard>
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        :routerLink="
                  customerId
                    ? {
                        name: 'customer-overview',
                        params: { customerId }
                      }
                    : {
                        name: 'customers'
                      }
                "
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="saving"
        submitForm
        v-test="'customer-submit'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModalLarge>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import apolloClient from '@/apollo/client';
import AddressFields from '@/components/_shared/address-fields/index.vue';
import { flash } from '@/helpers/ui';
import UploadControls from '@/modules/customers/components/UploadControls.vue';
import { useCompanyStore } from '@/stores/company';
import CustomerTags from '../CustomerTags.vue';
import { ASSIGN_CUSTOMER_TAGS } from '../graphql';
import { CREATE_CUSTOMER, GET_CUSTOMER, UPDATE_CUSTOMER } from './graphql';

const { company, companySettings } = useCompanyStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const props = defineProps<{
  parentRouteName: string;
}>();

const emit = defineEmits(['updated', 'created']);

const customer = reactive({
  address: '',
  alertNotes: '',
  blocked: false,
  city: '',
  custom1: '',
  custom10: '',
  custom2: '',
  custom3: '',
  custom4: '',
  custom5: '',
  custom6: '',
  custom7: '',
  custom8: '',
  custom9: '',
  customerNotes: '',
  dateOfBirth: null,
  email: '',
  firstName: '',
  gender: null,
  invoiceNotes: '',
  lastName: '',
  mobilePhone: '',
  personalRegistrationNumber: '',
  insuranceProvider: '',
  phone: '',
  picture: '',
  zipcode: '',
});

provide('customer', customer);

const loading = ref(false);
const customerTagIds = ref<number[]>([]);

const initialBSN = ref('');
const showBSNWarning = computed(
  () =>
    !loading.value && initialBSN.value !== customer.personalRegistrationNumber,
);

const heading = computed(() =>
  customerId.value
    ? t('global.actions.edit_customer')
    : t('customers.create_customer'),
);

const getCustomer = () => {
  loading.value = true;

  const { onResult } = useQuery(GET_CUSTOMER, {
    id: customerId.value,
  });

  onResult(({ data }) => {
    Object.keys(customer).forEach((key) => {
      if (data.customer[key]) {
        customer[key] = data.customer[key];
      }
    });

    if (data.customer.customerTags) {
      customerTagIds.value = data.customer.customerTags.map(
        (tag: any) => tag.id,
      );
    }

    initialBSN.value = customer.personalRegistrationNumber;
    loading.value = false;
  });
};

const customerId = computed(() =>
  route && typeof route.params.customerId === 'string'
    ? Number.parseInt(route.params.customerId)
    : null,
);

if (customerId.value) {
  getCustomer();
}

const saving = ref(false);
const emailInvalid = ref(false);

const submit = () => {
  saving.value = true;
  emailInvalid.value = false;

  if (!customer.dateOfBirth) {
    // When setting a date and then clearing it, the value becomes an empty string
    // But the backend doesn't like empty strings for the DateTime type
    // We should ideally fix this in the backend
    customer.dateOfBirth = null;
  }

  if (customerId.value) {
    const { mutate } = useMutation(UPDATE_CUSTOMER);
    mutate({
      input: {
        id: customerId.value,
        customerAttributes: customer,
      },
    }).then(() => {
      updateTags(customerId.value).then(() => {
        flash(t('global.flash.customer_updated'));
        emit('updated');

        apolloClient.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'customersOverview',
        });
        apolloClient.cache.gc();

        router.push({
          name: 'customer-overview',
          params: {
            customerId: customerId.value,
          },
        });
      });
    });
  } else {
    const { mutate } = useMutation(CREATE_CUSTOMER);
    mutate({
      input: {
        customerAttributes: customer,
      },
    }).then(
      ({
        data: {
          createCustomer: { customer, errors },
        },
      }) => {
        if (errors) {
          saving.value = false;
          if (errors.email?.length) {
            emailInvalid.value = true;
          }
          return;
        } else {
          updateTags(customer.id).then(() => {
            flash(t('global.flash.customer_created'));
            emit('created');

            apolloClient.cache.evict({
              id: 'ROOT_QUERY',
              fieldName: 'customersOverview',
            });
            apolloClient.cache.gc();

            router.push({
              name: 'customer-overview',
              params: {
                customerId: customer.id,
              },
            });
          });
        }
      },
    );
  }
};

const updateTags = (customerId: number) => {
  const { mutate } = useMutation(ASSIGN_CUSTOMER_TAGS);

  return mutate({
    input: {
      customerId,
      tagIds: customerTagIds.value,
    },
  });
};
</script>
